import { useCallback, useEffect, useState } from "react";

/**
 * usePwaInstall — single source of truth for PWA install state.
 *
 * The `beforeinstallprompt` event is captured in index.html (before React
 * mounts) and stashed on `window.__pwaInstall`. This hook reads that stash and
 * subscribes to the custom `pwa-installable` / `pwa-installed` events so any
 * component can offer a native install with no risk of missing the event.
 *
 * Returns:
 *   canInstall   — Chrome/Edge/Samsung native prompt is available now
 *   installed    — app is already running as an installed PWA (standalone)
 *   isIOS        — iOS Safari (no native prompt; needs Add to Home Screen)
 *   promptInstall() — triggers the native prompt; resolves to
 *                     "accepted" | "dismissed" | "ios" | "unavailable"
 */
const isStandalone = () =>
  (typeof window !== "undefined" &&
    ((window.matchMedia &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches)) ||
      window.navigator.standalone === true));

const detectIOS = () =>
  typeof window !== "undefined" &&
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
  !window.MSStream;

export function usePwaInstall() {
  const [canInstall, setCanInstall] = useState(
    !!(typeof window !== "undefined" && window.__pwaInstall && window.__pwaInstall.event)
  );
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onInstalled = () => {
      setCanInstall(false);
      setInstalled(true);
    };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    // The event may have been captured before this hook mounted.
    if (window.__pwaInstall && window.__pwaInstall.event) setCanInstall(true);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = window.__pwaInstall && window.__pwaInstall.event;
    if (evt) {
      evt.prompt();
      const choice = await evt.userChoice;
      window.__pwaInstall.event = null; // a prompt event can only be used once
      setCanInstall(false);
      return choice.outcome; // "accepted" | "dismissed"
    }
    return detectIOS() ? "ios" : "unavailable";
  }, []);

  return { canInstall, installed, isIOS: detectIOS(), promptInstall };
}

export default usePwaInstall;
